import { createContext, useContext } from 'react';
import styled from 'styled-components';

import { MappingAttention, MappingType } from 'services/api/admin/mappings';
import { colors } from 'styles/theme/colors';

import { Chip } from './Chips';
import { PendingOperation } from './operations';

type Value = { attention: MappingAttention[]; operations: PendingOperation[] };

export const MappingAttentionContext = createContext<Value>({ attention: [], operations: [] });

export default function MappingAttentionChips({ type }: { type: MappingType }) {
  const { attention, operations } = useContext(MappingAttentionContext);
  const counts = attention.find((a) => a.type === type);
  const pending = operations.filter((o) => o.type === type).length;

  if (!counts?.needsReview && !counts?.unmapped && !pending) return null;

  return (
    <Chips>
      {!!counts?.needsReview && <Chip $tone='red'>{counts.needsReview} need review</Chip>}
      {!!counts?.unmapped && <Chip $tone='blue'>{counts.unmapped} unmapped</Chip>}
      {pending > 0 && <Chip $tone='amber'>{pending} pending</Chip>}
    </Chips>
  );
}

// Integration configs often have no column row for a type the EHR still sends, so its values need their own way in
export function UnlistedMappingAttention({
  type,
  label,
  onOpen,
}: {
  type: MappingType;
  label: string;
  onOpen: () => void;
}) {
  const { attention, operations } = useContext(MappingAttentionContext);
  const counts = attention.find((a) => a.type === type);
  const pending = operations.some((o) => o.type === type);

  if (!counts?.needsReview && !counts?.unmapped && !pending) return null;

  return (
    <UnlistedLine>
      <OpenLink onClick={onOpen}>Edit {label} mappings</OpenLink>
      <MappingAttentionChips type={type} />
    </UnlistedLine>
  );
}

const UnlistedLine = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const OpenLink = styled.a`
  color: ${colors.blue100};
  cursor: pointer;
  font-size: 14px;
`;

const Chips = styled.span`
  display: inline-flex;
  gap: 6px;
  align-items: center;
  margin-left: 8px;
`;
